import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Card,
  Typography,
  TextField,
  Button,
  useTheme,
  IconButton,
  Snackbar,
  Alert,
  CircularProgress,
  Dialog,
  DialogContent,
} from "@mui/material";
import ArrowBackIosNewOutlined from "@mui/icons-material/ArrowBackIosNewOutlined";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import { useNavigate } from "react-router-dom";
import { entity_id, locationGeo, Platform } from "../../Utils/Const";
import { baseapi } from "../../Environmentalurl";
import DashboardHeader from "../../Dashboard/DashboardHeader";
import api from "../../Utils/Api";

const OTP_LENGTH = 6;
const RESEND_SECONDS = 30;
const STATIC_MACHINE_ID = "0409634b9bc2de2c";

export default function Walletclosureotp() {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const navigate = useNavigate();

  const [otp, setOtp] = useState(new Array(OTP_LENGTH).fill(""));
  const inputRefs = useRef([]);

  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [timer, setTimer] = useState(RESEND_SECONDS);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("info");

  // success dialog
  const [successOpen, setSuccessOpen] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");

  const mobileNumber = JSON.parse(sessionStorage.getItem("mobileNumber") || "null");
  const email = JSON.parse(sessionStorage.getItem("Poiemail") || "null");
  const SessionID = JSON.parse(sessionStorage.getItem("SessionID") || "null");

  // ---------------- COUNTDOWN ----------------
  useEffect(() => {
    if (timer <= 0) return;
    const interval = setInterval(() => setTimer((prev) => prev - 1), 1000);
    return () => clearInterval(interval);
  }, [timer]);

  const showSnackbar = (message, severity = "info") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  // ---------------- LOGOUT ----------------
  const handleLogout = () => {
    // clear session + navigate to login
    // If you have an auth-context logout(), call it here instead.
    localStorage.clear();
    sessionStorage.clear();
    navigate("/login", { replace: true });
  };

  // ---------------- OTP INPUT HANDLERS ----------------
  const handleOtpChange = (e, index) => {
    const value = e.target.value.replace(/[^0-9]/g, "");
    if (!value) {
      const updated = [...otp];
      updated[index] = "";
      setOtp(updated);
      return;
    }
    const updated = [...otp];
    updated[index] = value[value.length - 1];
    setOtp(updated);
    if (index < OTP_LENGTH - 1) inputRefs.current[index + 1]?.focus();
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    const pasted = e.clipboardData.getData("text").replace(/[^0-9]/g, "");
    if (!pasted) return;
    const updated = new Array(OTP_LENGTH).fill("");
    pasted.slice(0, OTP_LENGTH).split("").forEach((d, i) => (updated[i] = d));
    setOtp(updated);
    const nextEmpty = updated.findIndex((d) => !d);
    inputRefs.current[nextEmpty === -1 ? OTP_LENGTH - 1 : nextEmpty]?.focus();
  };

  const otpValue = otp.join("");
  const isOtpComplete = otpValue.length === OTP_LENGTH;

  // ---------------- SUBMIT OTP ----------------
  const handleSubmit = async () => {
    if (!isOtpComplete) {
      showSnackbar("Please enter the complete OTP", "warning");
      return;
    }

    const payload = {
      ReqType: "TAAccountCloseValidateOTP",
      Statement: "VerifyEmailOTP",
      MobileNumber: mobileNumber,
      EmailID: email,
      EmailOTP: otpValue,
      Platform: Platform,
      GeoLocation: locationGeo,
      EntityID: entity_id,
      MachineID: STATIC_MACHINE_ID,
      SessionID: SessionID,
    };

    setLoading(true);
    try {
      const response = await api.post(`${baseapi}/accountclosurevalidateOtp`, payload);
      const { Response_Code, Message } = response.data;

      // accept both 1000 and 1001 as success
      if (String(Response_Code) === "1000" || String(Response_Code) === "1001") {
        setSuccessMsg(Message || "Your wallet has been closed successfully.");
        setSuccessOpen(true);
      } else {
        showSnackbar(Message || "Invalid OTP. Please try again.", "error");
        setOtp(new Array(OTP_LENGTH).fill(""));
        inputRefs.current[0]?.focus();
      }
    } catch (error) {
      console.error("API Error:", error);
      showSnackbar("Server error. Try again.", "error");
    } finally {
      setLoading(false);
    }
  };

  // ---------------- RESEND OTP ----------------
  const handleResend = async () => {
    if (timer > 0 || resending) return;

    const payload = {
      ReqType: "TAAccountCloseResendOTP",
      Statement: "ResendEmailOTP",
      MobileNumber: mobileNumber,
      Platform: Platform,
      SessionID: SessionID,
      GeoLocation: locationGeo,
      EntityID: entity_id,
      MachineID: STATIC_MACHINE_ID,
    };

    setResending(true);
    try {
      const response = await api.post(`${baseapi}/AccountClouserResendOtp`, payload);
      const { Response_Code, Message } = response.data;

      if (String(Response_Code) === "1000") {
        showSnackbar(Message || "OTP resent successfully", "success");
        setOtp(new Array(OTP_LENGTH).fill(""));
        inputRefs.current[0]?.focus();
        setTimer(RESEND_SECONDS);
      } else {
        showSnackbar(Message || "Unable to resend OTP", "error");
      }
    } catch (error) {
      console.error("API Error:", error);
      showSnackbar("Server error. Try again.", "error");
    } finally {
      setResending(false);
    }
  };

  const handleBack = () => navigate("/profile-settings/walletclouserreq");

  return (
    <>
      <DashboardHeader />
      <Box
        sx={{
          width: "100%",
          minHeight: "100vh",
          bgcolor: isDark ? "#0d1523" : "#f5f7fb",
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "center",
          py: 5,
          px: 2,
        }}
      >
        <Box sx={{ width: "100%", maxWidth: 520 }}>
          <Card
            sx={{
              borderRadius: 5,
              overflow: "hidden",
              boxShadow: "0px 12px 45px rgba(0,0,0,0.10)",
              bgcolor: isDark ? "#0b1220" : "#ffffff",
            }}
          >
            {/* Header bar */}
            <Box
              sx={{
                px: 3,
                py: 2.5,
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                background: "linear-gradient(135deg, #16a34a, #22c55e)",
              }}
            >
              <IconButton
                onClick={handleBack}
                sx={{
                  color: "#fff",
                  bgcolor: "rgba(255,255,255,0.18)",
                  "&:hover": { bgcolor: "rgba(255,255,255,0.28)" },
                }}
                size="small"
              >
                <ArrowBackIosNewOutlined fontSize="small" />
              </IconButton>
              <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: 18, flex: 1 }}>
                Verify OTP
              </Typography>
              <Box
                sx={{
                  px: 1.5,
                  py: 0.4,
                  borderRadius: 2,
                  fontSize: 12,
                  fontWeight: 600,
                  bgcolor: "rgba(255,255,255,0.2)",
                  color: "#fff",
                }}
              >
                Step 2 of 2
              </Box>
            </Box>

            <Box sx={{ p: { xs: 3, md: 4 } }}>
              {/* Info text */}
              <Box sx={{ textAlign: "center", mb: 4 }}>
                <Typography variant="h6" fontWeight={700} sx={{ color: isDark ? "#e2e8f0" : "#0f172a", mb: 1 }}>
                  Enter Verification Code
                </Typography>
                <Typography sx={{ color: isDark ? "#94a3b8" : "#64748b", fontSize: 14, lineHeight: 1.7 }}>
                  We've sent a 6-digit code to your registered mobile
                  {mobileNumber ? ` ••••••${String(mobileNumber).slice(-4)}` : ""}
                  {email ? ` and email ${email}` : ""}.
                </Typography>
              </Box>

              {/* OTP inputs */}
              <Box
                sx={{ display: "flex", justifyContent: "center", gap: 1.2, mb: 3 }}
                onPaste={handlePaste}
              >
                {otp.map((digit, index) => (
                  <TextField
                    key={index}
                    inputRef={(el) => (inputRefs.current[index] = el)}
                    value={digit}
                    onChange={(e) => handleOtpChange(e, index)}
                    onKeyDown={(e) => handleKeyDown(e, index)}
                    inputProps={{
                      maxLength: 1,
                      inputMode: "numeric",
                      pattern: "[0-9]*",
                      style: { textAlign: "center", fontSize: 22, fontWeight: 700 },
                    }}
                    sx={{
                      width: 52,
                      "& .MuiOutlinedInput-root": {
                        bgcolor: isDark ? "#0f172a" : "#f8fafc",
                        height: 58,
                        borderRadius: 3,
                        "& fieldset": { borderColor: digit ? "#22c55e" : "#e2e8f0" },
                        "&:hover fieldset": { borderColor: "#16a34a" },
                        "&.Mui-focused fieldset": { borderColor: "#16a34a" },
                      },
                    }}
                  />
                ))}
              </Box>

              {/* Resend */}
              <Box sx={{ textAlign: "center", mb: 3 }}>
                {timer > 0 ? (
                  <Typography sx={{ fontSize: 14, color: "#64748b" }}>
                    Resend code in <b>{timer}s</b>
                  </Typography>
                ) : (
                  <Button
                    onClick={handleResend}
                    disabled={resending}
                    sx={{ textTransform: "none", fontWeight: 700, color: "#16a34a" }}
                  >
                    {resending && <CircularProgress size={16} sx={{ mr: 1 }} />}
                    Resend OTP
                  </Button>
                )}
              </Box>

              {/* Submit */}
              <Button
                fullWidth
                onClick={handleSubmit}
                disabled={!isOtpComplete || loading}
                sx={{
                  bgcolor: "#16a34a",
                  color: "white",
                  py: 1.4,
                  borderRadius: 3,
                  fontWeight: 700,
                  fontSize: 16,
                  textTransform: "none",
                  "&:hover": { bgcolor: "#15803d" },
                  "&.Mui-disabled": { bgcolor: isDark ? "#1e293b" : "#e2e8f0" },
                }}
              >
                {loading ? <CircularProgress size={22} sx={{ color: "white" }} /> : "Verify & Close Wallet"}
              </Button>

              <Typography sx={{ mt: 2.5, fontSize: 12, color: "#94a3b8", textAlign: "center", lineHeight: 1.6 }}>
                Once verified, your wallet closure request will be processed and your remaining
                balance transferred to the provided bank account.
              </Typography>
            </Box>
          </Card>
        </Box>
      </Box>

      {/* SUCCESS DIALOG → OK logs out */}
      <Dialog
        open={successOpen}
        onClose={(e, reason) => {
          // prevent closing by backdrop/esc — force OK/logout
          if (reason === "backdropClick" || reason === "escapeKeyDown") return;
          setSuccessOpen(false);
        }}
        PaperProps={{ sx: { borderRadius: 4, width: "100%", maxWidth: 360, p: 1 } }}
      >
        <DialogContent sx={{ textAlign: "center", p: 4 }}>
          <Box
            sx={{
              width: 76,
              height: 76,
              borderRadius: "50%",
              mx: "auto",
              mb: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "linear-gradient(135deg, #22c55e, #16a34a)",
            }}
          >
            <CheckCircleRoundedIcon sx={{ fontSize: 44, color: "#fff" }} />
          </Box>

          <Typography fontSize={20} fontWeight={800} sx={{ color: "#111827" }}>
            Wallet Closed
          </Typography>
          <Typography fontSize={14} mt={1} sx={{ color: "#6b7280", lineHeight: 1.6 }}>
            {successMsg}
          </Typography>

          <Button
            fullWidth
            variant="contained"
            onClick={() => {
              setSuccessOpen(false);
              handleLogout();
            }}
            sx={{
              mt: 3,
              py: 1.2,
              borderRadius: 3,
              textTransform: "none",
              fontWeight: 700,
              bgcolor: "#16a34a",
              "&:hover": { bgcolor: "#15803d" },
            }}
          >
            OK
          </Button>
        </DialogContent>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity={snackbarSeverity} sx={{ width: "100%" }} onClose={() => setSnackbarOpen(false)}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
}